import { APIObject } from "@/services/api/types";
import { ReservedUserIds, UserData } from "@/entities/users/types";
import { DreamerAttachmentFilterMetadata } from "@/entities/attachments/types";
import { MeetingPrepBrief } from "@/entities/meetingPrep/types";
import { DraftEmailPayload } from "@/entities/dreamerEmail/types";

export const MAX_MESSAGES = 500;

export const enum ReservedMessageIds {
  WAITING_MSG = "waiting-msg",
  MSG_SEPARATOR = "msg-separator"
}

// Prefixes the retry card shown under a question that was never answered,
// followed by that question's id.
export const NO_ANSWER_MSG_PREFIX = "no-answer-";

export const enum FixedMessageIds {
  NEW_MESSAGE = "***new-single-message***",
  NEW_MESSAGES = "***new-messages***"
}

export type MessageLikeStatus = boolean | null;

interface SendMessageRequest {
  message: string;
  requestId?: string;
  // Re-asks this earlier question of the user's after a failed answer. The
  // server answers it from its stored copy and adds no new question row.
  retryMessageId?: string;
  currentDatetime?: string; // ISO 8601 with timezone offset
  authToken?: string;
}

interface MessagePayload extends APIObject {
  messageId: string;
  fromUserId: string;
  message: string;
  timestamp: string;
  isStarred?: boolean;
  isLiked?: MessageLikeStatus;
  isRead?: boolean;
  requestId?: string;
  tabularMetadata?: DreamerAttachmentFilterMetadata | null;
  isStopped?: boolean;
  isSchedulable?: boolean;
  // Whether the lineage can be run again, resolved server-side from the
  // originating message. isSchedulable is the row's own flag and is false on
  // every message the scheduler or Run now produced.
  isReplayable?: boolean;
  // The reports_schedules row behind this answer, present from the moment the
  // user saves it. scheduleId is the same row once it also has a cadence, so a
  // saved report has savedReportId and no scheduleId.
  savedReportId?: string | null;
  scheduleId?: string | null;
  // What the user currently calls that report. Distinct from the chart's own
  // title inside tabularMetadata, which the brain generated when the data was
  // produced and which a rename cannot reach.
  reportName?: string | null;
  content?: MessageContent;
  emailDraft?: DraftEmailPayload;
  // A failed answer: broadcast live and never stored, so it is absent on reload.
  isError?: boolean;
}

interface MessagesResponse {
  messages: MessagePayload[];
  requestId?: string;
}

export interface MessageStatusUpdatePayload {
  isStarred?: boolean;
  isLiked?: MessageLikeStatus;
}

export type InlineChatAction = "manage-scheduled-reports";

// Prefill values for the manual lead-creation flow, carried by the
// dreamer_lead_create_manual affordance.
export interface LeadPrefill {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
}

// Push-originated affordance attached to a proactively-pushed message
// (e.g. the "Undo" on a Dreamer-created task, or the "Create now" / "Not a
// lead" pair on a lead suggestion). Discriminated by `type`.
export interface PushAction {
  type:
    | "dreamer_task_undo"
    | "dreamer_task_reopen"
    | "dreamer_lead_create"
    | "dreamer_lead_create_manual"
    | "dreamer_lead_convert"
    | (string & Record<never, never>);
  taskId?: string;
  candidateId?: string;
  leadId?: string;
  leadPrefill?: LeadPrefill;
  action?: never;
  params?: never;
}

// Known brain action strings — exported so consumers can reference the constant
// rather than duplicating the magic string.
export const BRAIN_ACTIONS = {
  REPORTING_SCHEDULER_FILTER: "reporting.scheduler.filter",
} as const;

// Brain-emitted server instruction attached to a chat response. The client
// reads `action` and calls the corresponding API itself (e.g.
// BRAIN_ACTIONS.REPORTING_SCHEDULER_FILTER → fetch + render the schedules inline).
// Discriminated by `action`.
export interface MessageAction {
  // Derived from BRAIN_ACTIONS so adding a new action constant widens the type automatically.
  action: typeof BRAIN_ACTIONS[keyof typeof BRAIN_ACTIONS] | (string & Record<never, never>);
  params?: Record<string, unknown>;
  type?: never;
  taskId?: never;
}

// A message carries at most one action; the two producers (push vs brain) are
// disjoint shapes sharing the same field, told apart by their required key.
export type ChatAction = PushAction | MessageAction;

// Guards are mutually exclusive and check the discriminator is a string, so a
// malformed payload carrying both keys can't satisfy both.
export const isPushAction = (action?: ChatAction): action is PushAction =>
  !!action && typeof action.type === "string" && !("action" in action);

export const isMessageAction = (action?: ChatAction): action is MessageAction =>
  !!action && typeof action.action === "string" && !("type" in action);

// Discriminated envelope for typed content attached to a message. Mirrors the
// server MessageContent: `kind` selects which payload the client renders (an
// action affordance, the meeting-prep card, ...).
export interface MessageContent {
  kind: "action" | "meeting_prep" | (string & Record<never, never>);
  action?: ChatAction;
  meetingPrep?: MeetingPrepBrief;
}

export interface ChatMessage {
  id: string;
  user: UserData | undefined;
  message: string;
  timestamp: string;
  isStarred?: boolean;
  isLiked?: MessageLikeStatus;
  isRead?: boolean;
  thoughtMessage?: string;
  requestId?: string;
  tabularMetadata?: DreamerAttachmentFilterMetadata | null;
  isStopped?: boolean;
  isStoppedResponse?: boolean;
  // Dreamer failed to answer the question before it; renders as the retry card.
  isError?: boolean;
  isSchedulable?: boolean;
  isReplayable?: boolean;
  savedReportId?: string | null;
  scheduleId?: string | null;
  // What the user currently calls that report. Distinct from the chart's own
  // title inside tabularMetadata, which the brain generated when the data was
  // produced and which a rename cannot reach.
  reportName?: string | null;
  // Set only on the client-built notice of a finished report run, which never
  // enters the chat feed; clicking the notice opens that report.
  reportRunId?: string | null;
  inlineAction?: InlineChatAction;
  content?: MessageContent;
  emailDraft?: DraftEmailPayload;
}

export const dreamUser: UserData = {
  id: ReservedUserIds.DREAMER_CONVERSATIONAL_ID,
  firstName: "Dream",
  lastName: "of the Endless",
  displayName: "Dreamer",
  department: 1,
  email: "dream@endless.org",
  lastLoginAt: "2021-07-08T15:46:09Z",
  timezone: "",
  role: 1,
  jobTitle: "Visionary Leader",
  avatarUrl: "/chat-dreamer.png",
  groupIds: ["G-FIRS-1111"]
};

export const mentorUser: UserData = {
  id: ReservedUserIds.MENTOR_CONVERSATIONAL_ID,
  firstName: "Destiny",
  lastName: "of the Endless",
  displayName: "Destiny",
  department: 1,
  email: "destiny@endless.org",
  lastLoginAt: "2021-07-08T15:46:09Z",
  timezone: "",
  role: 1,
  jobTitle: "Visionary Leader",
  avatarUrl: "/chat-alerts.png",
  groupIds: ["G-FIRS-1111"]
};

export const alertsUser: UserData = {
  id: ReservedUserIds.ALERTS_CONVERSATIONAL_ID,
  firstName: "Destruction",
  lastName: "of the Endless",
  displayName: "Destruction",
  department: 1,
  email: "destruction@endless.org",
  lastLoginAt: "2021-07-08T15:46:09Z",
  timezone: "",
  role: 1,
  jobTitle: "The Prodigal",
  avatarUrl: "/chat-alerts.png",
  groupIds: ["G-FIRS-1111"]
};

export const notificationsUser: UserData = {
  id: ReservedUserIds.NOTIFICATIONS_CONVERSATIONAL_ID,
  firstName: "Delight",
  lastName: "of the Endless",
  displayName: "Delight",
  department: 1,
  email: "delight@endless.org",
  lastLoginAt: "2021-07-08T15:46:09Z",
  timezone: "",
  role: 1,
  jobTitle: "Bearer of Good News",
  avatarUrl: "/chat-notifications.png",
  groupIds: ["G-FIRS-1111"]
};

export const DATE_SEPARATOR_PREFIX = "date-separator-";

/**
 * A websocket frame telling this user's open tabs that something happened
 * which is not a chat message. Not stored: a tab that is not connected when it
 * is sent never sees it. `type` picks the payload's shape.
 */
export interface UserEventFrame {
  event: { type: string; payload: unknown };
}

export type UserEventHandler = (payload: unknown) => void;

export type { SendMessageRequest, MessagesResponse, MessagePayload };
